import Vue from 'vue';
import axios from 'axios';
import router from '../router';

const initializeBackendAxios = () => {
  const backendAxios = axios.create({
    baseURL: process.env.VUE_APP_BACKEND_URL,
    withCredentials: true,
  });
  // backendAxios.interceptors.response.use(
  //   response => response,
  //   error => error.response,
  // );

  return backendAxios;
};

export default {
  namespaced: true,
  state: {
    authenticated: false,
    backendAxios: initializeBackendAxios(),
    isLoginDialogVisible: false,
    socialNetworks: [{
      id: 'facebook',
      name: 'Facebook',
      icon: 'facebook-square',
    }, {
      id: 'google',
      name: 'Google',
      icon: 'google',
    }, {
      id: 'linkedin',
      name: 'Linkedin',
      icon: 'linkedin',
    }, {
      id: 'twitter',
      name: 'Twitter',
      icon: 'twitter-square',
    }, {
      id: 'twitch',
      name: 'Twitch',
      icon: 'twitch',
    }],
  },
  mutations: {
    setAuthDialogVisibility(state, visibility) {
      state.isLoginDialogVisible = visibility;
    },
  },
  actions: {
    // tries to set the user, but does it silently without alerting the user
    async trySetUser({ state, commit }) {
      // do nothing if user is already authenticated
      if (state.authenticated) {
        return;
      }
      try {
        const userData = await state.backendAxios.get('/auth/user');
        if (userData && userData.data) {
          state.authenticated = true;
          commit('user/setUser', userData.data, { root: true });
        }
      } catch (err) {
        console.log('No user found in session');
      }
    },
    async login({ state, commit }, { email, password }) {
      try {
        const userData = await state.backendAxios.post('/auth/local/login', {
          username: 'foo', // we do not ask for username on signup. for now...
          email,
          password,
        });
        if (userData && userData.data) {
          state.authenticated = true;
          commit('user/setUser', userData.data, { root: true });
          commit('setAuthDialogVisibility', false);
        }
      } catch (err) {
        Vue.toasted.show(err.response.data, {
          type: 'info',
          duration: 2000,
        });
      }
    },
    async signup({ state }, { displayName, email, password }) {
      try {
        await state.backendAxios.post('/auth/local/signup', {
          displayName,
          email,
          password,
        });
      } catch (err) {
        Vue.toasted.show(err.response.data, {
          type: 'info',
          duration: 2000,
        });
      }
    },
    async authenticate({ state, commit, dispatch }, connection) {
      // do nothing if user is already authenticated
      Vue.$ga.event('auth', connection, 'start', 1);
      if (state.authenticated) {
        return true;
      }

      Vue.$ga.event('auth', connection, 'alreadyAuthenticated', 1);
      // first try to retrieve the user from session
      await dispatch('trySetUser');
      if (state.authenticated) {
        return true;
      }

      Vue.$ga.event('auth', connection, 'retrieveFromSession', 1);
      // if we do not have a connection, pop the dialog with various login options
      if (!connection) {
        Vue.$ga.event('auth', connection, 'popupDialogOptions', 1);
        commit('setAuthDialogVisibility', true);
        return false;
      }

      Vue.$ga.event('auth', connection, 'startOauthRedirect', 1);
      // Do the oauth flow for the chosen connection type
      // if redirectUri is not set, default to home page
      const redirectUri = router.currentRoute.path || '/';
      const decodedState = { redirectUri };
      const encodedState = encodeURIComponent(JSON.stringify(decodedState));
      window.location =
        `${process.env.VUE_APP_BACKEND_URL}/auth/${connection}/state/${encodedState}`;
      commit('setAuthDialogVisibility', true);
      return false;
    },

    async logout({ state, commit }) {
      Vue.$ga.event('auth', 'click', 'logout', 1);
      await state.backendAxios.get('/auth/logout');
      state.authenticated = false;
      commit('user/clearUser', null, { root: true });
      router.push('/');
      Vue.toasted.show('You have Logged Out', {
        type: 'info',
        duration: 2000,
      });
    },

    async handleAuthentication({ state, commit }, {
      // eslint-disable-next-line
      connection, code, oauth_token, oauth_verifier, encodedState,
    }) {
      // eslint-disable-next-line
      if (!code && !oauth_token && !oauth_verifier) {
        Vue.$ga.event('auth', connection, 'failOauthRedirect', 1);
        console.error('User did not authenticate');
        router.push('/');
        return;
      }
      let redirectUri = '/';
      if (encodedState) {
        const decodedState = JSON.parse(encodedState);
        ({ redirectUri } = decodedState);
      } else if (state.authenticated) {
        // if we are connecting some network to this user account
        // we hardcode the redirectUri since twitter uses lame oauth 1 which does not use state.
        redirectUri = '/account/social';
      }
      try {
        // eslint-disable-next-line
        const userData = await state.backendAxios.get(`/auth/${connection}/callback?code=${code}&oauth_token=${oauth_token}&oauth_verifier=${oauth_verifier}`);
        if (userData && userData.data) {
          state.authenticated = true;
          commit('user/setUser', userData.data, { root: true });
          commit('setAuthDialogVisibility', false);
          Vue.$ga.event('auth', connection, 'completeOauthRedirect', 1);
        }
      } catch (err) {
        commit('user/clearUser', null, { root: true });
        Vue.toasted.show(err.response.data, {
          type: 'info',
          duration: 2000,
        });
        Vue.$ga.event('auth', connection, 'failOauthRedirect', 1);
      }
      router.push(redirectUri);
    },

    backendValidator: async ({ state }, data) => {
      try {
        const [key, value] = Object.entries(data)[0];
        await state.backendAxios.post(`/validate/${key}/`, {
          [key]: value,
        });
        return true;
      } catch (err) {
        return err.response;
      }
    },
  },
};
